import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React from "react";
import { DropResult } from "react-beautiful-dnd";
import { COLOR_GREY_9 } from "shared/theme/colors";
import NoTableItemsMessage from "../Table/NoTableItemsMessage";
import { StyledHeader, StyledPaper } from "../Table/styled";
import DraggableRow from "./components/DraggableRow";
import DroppableTable from "./components/DroppableTable";
import { Column, reorder } from "./helpers";
import {
  StyledEmptyWrapper,
  StyledSpinnerWrapper,
  StyledTable,
} from "./styled";

type TableProps<T> = {
  title?: string;
  items: T[];
  columns: Array<Column<T>>;
  isItemsFetched: boolean;
  isItemsLoading: boolean;
  noItemsMessage?: React.ReactNode;
  actions?: React.ReactNode;
  emptyListMessage?: React.ReactNode;
  onItemsReorder: (items: Array<T>) => void;
  reorderDisabled?: boolean;
  onRowClick?: (item: T) => void;
  getRowOpacity?: (item: T) => number | undefined;
  onRowMouseEnter?: (item: T) => void;
  onRowMouseLeave?: () => void;
};

export default function DragNDropTable<T extends { id: string }>({
  title,
  items,
  columns,
  isItemsLoading = true,
  isItemsFetched,
  noItemsMessage,
  emptyListMessage,
  onItemsReorder,
  actions,
  reorderDisabled,
  onRowClick,
  getRowOpacity,
  onRowMouseEnter,
  onRowMouseLeave,
}: TableProps<T>) {
  const onDragEnd = async (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const updated = reorder(
      items,
      result.source.index,
      result.destination.index,
    ) as Array<T>;

    onItemsReorder(updated);
  };

  const isLoading = isItemsLoading && !isItemsFetched;

  if (!isItemsLoading && !items.length) {
    return (
      <StyledPaper>
        <StyledEmptyWrapper>
          <Typography variant="bodyMMedium" color={COLOR_GREY_9}>
            {emptyListMessage}
          </Typography>
        </StyledEmptyWrapper>
      </StyledPaper>
    );
  }

  if (!isLoading && !items?.length) {
    return (
      <StyledPaper>
        {title && (
          <StyledHeader>
            <Typography>{title}</Typography>
            <Box>{actions}</Box>
          </StyledHeader>
        )}
        <Box>
          <NoTableItemsMessage message={noItemsMessage ?? ""} />
        </Box>
      </StyledPaper>
    );
  }

  return (
    <StyledTable loading={isLoading}>
      <TableHead>
        <TableRow>
          {!reorderDisabled && <TableCell />}
          {columns.map((column) => (
            <TableCell
              key={column.headerName}
              align={column.headerAlign as TableCellProps["align"]}
              padding={column.headerPadding as TableCellProps["padding"]}
              style={{
                fontWeight: 600,
                padding: "0.75rem",
                width: column.width,
              }}
            >
              {column.headerName}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      {DroppableTable(onDragEnd)({
        children: items.map((item, i, arr) => {
          const opacity = getRowOpacity ? getRowOpacity(item) : undefined;

          const itemWithOpacity = {
            ...item,
            opacity,
          };

          return (
            <DraggableRow
              key={item.id}
              draggableHidden={reorderDisabled}
              disabled={reorderDisabled || isItemsLoading || arr.length < 2}
              index={i}
              isLast={i === arr.length - 1}
              item={itemWithOpacity}
              columns={columns}
              onRowClick={onRowClick}
              onMouseEnter={() => onRowMouseEnter?.(item)}
              onMouseLeave={() => onRowMouseLeave?.()}
            />
          );
        }),
      })}
      {isLoading && (
        <StyledSpinnerWrapper>
          <CircularProgress />
        </StyledSpinnerWrapper>
      )}
    </StyledTable>
  );
}

DragNDropTable.defaultProps = {
  title: "",
  actions: null,
  noItemsMessage: null,
};
